<template>
  <transition name="pycad-panel">
    <aside
      v-if="ui.isPanelOpen && headerDef"
      class="pycad-right-panel"
      :style="{ width: ui.panelWidth + 'px' }"
    >
      <!-- Resize handle -->
      <div class="pycad-right-panel__resize" @mousedown.prevent="startResize">
        <i class="mdi mdi-drag-vertical pycad-right-panel__grip"></i>
      </div>

      <!-- Tab bar (panoramic workspace only) -->
      <div v-if="isPanoramicMode" class="pycad-right-panel__tabs">
        <button
          type="button"
          class="pycad-right-panel__tab"
          :class="{
            'pycad-right-panel__tab--active':
              ui.panoramicInspectorTab === 'panoramic',
          }"
          @click="ui.focusPanoramicInspectorTab('panoramic')"
        >
          <i :class="['mdi', workspaceInspector?.inspectorIcon]"></i>
          <span>{{ workspaceInspector?.inspectorLabel }}</span>
        </button>
        <button
          v-if="secondaryDef"
          type="button"
          class="pycad-right-panel__tab"
          :class="{
            'pycad-right-panel__tab--active':
              ui.panoramicInspectorTab === 'utility',
          }"
          @click="ui.focusPanoramicInspectorTab('utility')"
        >
          <i :class="['mdi', secondaryDef.icon]"></i>
          <span>{{ secondaryDef.label }}</span>
        </button>
      </div>

      <!-- Header -->
      <div class="pycad-right-panel__header">
        <i
          :class="['mdi', headerDef.icon, 'pycad-right-panel__header-icon']"
        ></i>
        <span class="pycad-right-panel__title">{{ headerDef.label }}</span>
        <button
          v-if="showCloseButton"
          type="button"
          class="pycad-right-panel__close"
          @click="onClose"
        >
          <i class="mdi mdi-close"></i>
        </button>
      </div>

      <!-- Body -->
      <div class="pycad-right-panel__body">
        <template v-if="isPanoramicMode">
          <div
            v-show="ui.panoramicInspectorTab === 'panoramic'"
            class="pycad-right-panel__pane"
          >
            <component
              :is="workspaceInspector?.inspectorComponent"
              v-if="workspaceInspector?.inspectorComponent"
            />
          </div>
          <div
            v-show="ui.panoramicInspectorTab === 'utility'"
            class="pycad-right-panel__pane"
          >
            <component
              :is="secondaryComponent"
              v-if="secondaryComponent && ui.panoramicSecondaryPanel"
              :key="ui.panoramicSecondaryPanel"
            />
            <div v-else class="pycad-right-panel__empty">
              Panel not available
            </div>
          </div>
        </template>
        <template v-else>
          <component
            :is="standardComponent"
            v-if="standardComponent && activePanel"
            :key="activePanel"
          />
          <div v-else class="pycad-right-panel__empty">Panel not available</div>
        </template>
      </div>
    </aside>
  </transition>
</template>

<script setup lang="ts">
import { computed } from 'vue';
import { storeToRefs } from 'pinia';

import { usePycadUiStore, type PycadPanelId } from '@/src/store/pycad-ui';
import { enabledPanels, enabledWorkspaces } from '@/src/features/registry';

const ui = usePycadUiStore();
const { activePanel, curvedMPRWorkspaceActive } = storeToRefs(ui);

const PANELS = enabledPanels();

const panelById = computed(() => {
  const map: Partial<Record<PycadPanelId, (typeof PANELS)[number]>> = {};
  PANELS.forEach((p) => {
    map[p.id] = p;
  });
  return map;
});

const isPanoramicMode = computed(() => curvedMPRWorkspaceActive.value);

const workspaceInspector = computed(() => {
  if (!isPanoramicMode.value) return null;
  return enabledWorkspaces().find((ws) => ws.id === 'implantPlanning') ?? null;
});

const secondaryDef = computed(() => {
  const id = ui.panoramicSecondaryPanel;
  return id ? (panelById.value[id] ?? null) : null;
});

const headerDef = computed(() => {
  if (isPanoramicMode.value) {
    if (ui.panoramicInspectorTab === 'utility' && secondaryDef.value) {
      return secondaryDef.value;
    }
    if (workspaceInspector.value) {
      return {
        icon: workspaceInspector.value.inspectorIcon,
        label: workspaceInspector.value.inspectorLabel,
      };
    }
    return null;
  }
  return activePanel.value
    ? (panelById.value[activePanel.value] ?? null)
    : null;
});

const standardComponent = computed(
  () =>
    (activePanel.value
      ? panelById.value[activePanel.value]?.component
      : null) ?? null
);

const secondaryComponent = computed(
  () => secondaryDef.value?.component ?? null
);

const showCloseButton = computed(() => {
  if (!isPanoramicMode.value) return true;
  return (
    ui.panoramicInspectorTab === 'utility' &&
    ui.panoramicSecondaryPanel !== null
  );
});

function onClose() {
  if (isPanoramicMode.value) {
    ui.closePanoramicUtilityPanel();
    return;
  }
  ui.closePanel();
}

// --- Resize behaviour --- //
function startResize(e: MouseEvent) {
  const startX = e.clientX;
  const startWidth = ui.panelWidth;

  function onMouseMove(ev: MouseEvent) {
    const delta = startX - ev.clientX;
    ui.setPanelWidth(startWidth + delta);
  }

  function onMouseUp() {
    document.removeEventListener('mousemove', onMouseMove);
    document.removeEventListener('mouseup', onMouseUp);
  }

  document.addEventListener('mousemove', onMouseMove);
  document.addEventListener('mouseup', onMouseUp);
}
</script>

<style scoped>
.pycad-right-panel {
  position: relative;
  display: flex;
  flex-direction: column;
  flex-shrink: 0;
  height: 100%;
  background: var(--pycad-surface);
  border-left: 1px solid var(--pycad-border);
  overflow: hidden;
  min-width: 0;
  font-family: 'Inter', system-ui, sans-serif;
  color: var(--pycad-text-primary);
}

.pycad-right-panel__resize {
  position: absolute;
  top: 0;
  bottom: 0;
  left: 0;
  width: 6px;
  cursor: col-resize;
  z-index: 10;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: background-color 0.15s;
}

.pycad-right-panel__resize:hover {
  background-color: rgba(255, 153, 0, 0.4);
}

.pycad-right-panel__grip {
  font-size: 12px;
  color: var(--pycad-text-muted);
  opacity: 0;
  transition: opacity 0.15s;
}

.pycad-right-panel__resize:hover .pycad-right-panel__grip {
  opacity: 1;
}

.pycad-right-panel__tabs {
  display: flex;
  align-items: stretch;
  gap: 2px;
  padding: 6px 8px 0;
  border-bottom: 1px solid var(--pycad-border);
  flex-shrink: 0;
}

.pycad-right-panel__tab {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 8px 10px;
  border: none;
  border-bottom: 2px solid transparent;
  background: transparent;
  color: var(--pycad-text-muted);
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.15s;
  outline: none;
  max-width: 50%;
}

.pycad-right-panel__tab:hover {
  color: var(--pycad-text-primary);
}

.pycad-right-panel__tab--active {
  color: var(--pycad-primary);
  border-bottom-color: var(--pycad-primary);
}

.pycad-right-panel__header {
  height: 44px;
  min-height: 44px;
  display: flex;
  align-items: center;
  gap: 9px;
  padding: 0 14px;
  border-bottom: 1px solid var(--pycad-border);
  flex-shrink: 0;
}

.pycad-right-panel__header-icon {
  color: var(--pycad-primary);
  font-size: 15px;
  flex-shrink: 0;
}

.pycad-right-panel__title {
  flex: 1;
  font-size: 13px;
  font-weight: 600;
  letter-spacing: 0.03em;
  text-transform: uppercase;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.pycad-right-panel__close {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  border-radius: 4px;
  background: transparent;
  border: none;
  cursor: pointer;
  color: var(--pycad-text-muted);
  transition: all 0.15s;
  outline: none;
  padding: 0;
}

.pycad-right-panel__close:hover {
  background: var(--pycad-elevated);
  color: var(--pycad-text-primary);
}

.pycad-right-panel__body {
  flex: 1 1 auto;
  overflow-y: auto;
  padding: 8px;
  background: var(--pycad-surface);
  min-height: 0;
}

.pycad-right-panel__pane {
  min-height: 0;
}

.pycad-right-panel__empty {
  padding: 24px;
  text-align: center;
  font-size: 12px;
  color: var(--pycad-text-muted);
}

.pycad-panel-enter-active,
.pycad-panel-leave-active {
  transition:
    transform 200ms ease,
    opacity 200ms ease;
}
.pycad-panel-enter-from,
.pycad-panel-leave-to {
  transform: translateX(100%);
  opacity: 0;
}
</style>
